import { Column } from "primereact/column";
import { DataTable, DataTableProps } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import * as React from "react";
import ChangeEnum from "../../interfaces/frontend/ChangeEnum";
import IResultInhibitor from "../../interfaces/frontend/IResultInhibitor";
import { InhibitorSelectDiff } from "./InhibitorSelectDiff";
import Icons from "../Icons";
import classNames from "classnames";
import { useCallback, useEffect, useMemo, useState } from "react";
import { debounce } from "../../../../utils/tools";

interface Props {
  screenInhibitors: IResultInhibitor[],
  printInhibitors?: IResultInhibitor[],

  emptyMessage (globalFilter: string | null): string,
  scrollHeight?: string,
  className?: string,
  columnsBefore?: React.ReactNode | React.ReactNode[],
  columnsAfter?:  React.ReactNode | React.ReactNode[],
  dataTableProps?: Partial<Omit<DataTableProps<IResultInhibitor[]>, "className" | "emptyMessage">>,

  differenceFilter: ChangeEnum | null,
  setDifferenceFilter(differenceFilter: ChangeEnum | null): void,
}

function InhibitorBaseTable({
  dataTableProps = {},
  ...props
}: Props) {
  
  const [globalFilter, setGlobalFilter] = useState('');

  const filteredScreenInhibitors = useFilteredInhibitors(props.screenInhibitors, globalFilter);

  const onGlobalFilterInput = useCallback(e => {
    setGlobalFilter(e.target.value)
  }, []);

  const clearGlobalFilter = useCallback(() => {
    setGlobalFilter('');
  }, []);

  const databaseBodyTemplate = useCallback((data: IResultInhibitor) => {
    if (data.databaseLink !== null) {
      const { drugId, database, label } = data.databaseLink;
      const url = database === 'drugbank' ? `https://go.drugbank.com/drugs/${drugId}` :
        database === 'zinc' ? `https://zinc.docking.org/substances/${drugId}/` : undefined;
      return <a href={url} target="_blank">{label || drugId}</a>
    }
  }, []);

  const nameBodyTemplate = useCallback((data: IResultInhibitor) => {
    if (!data.associatedDrug) {
      return data.name;
    } else {
      return <>
        <span>{data.name}</span>
        {Icons.associatedDrug}
      </>
    }
  }, []);

  function renderHeader() {
    return <div className="ll-inhibitor-header">
      <span className="ll-inhibitor-search p-input-icon-left p-input-icon-right">
        <i className="pi pi-search" />
        <i className="pi pi-times" onClick={clearGlobalFilter} style={{
          display: globalFilter ? 'block' : 'none',
          paddingRight: '5px',
          cursor: 'pointer'
        }} />
        <InputText
          type="search"
          value={globalFilter}
          onInput={onGlobalFilterInput}
          placeholder="Search inhibitor..."
          style={{ width: "100%" }}
        />
      </span>
      <InhibitorSelectDiff value={props.differenceFilter} setValue={props.setDifferenceFilter} />
    </div>
  }

  // Header (with search input) must be rendered separately -- NOT within DataTable's header param,
  // because we use globalFilter in a key so that DataTable is rerendered each time the global filter changes.
  // If the header was inside DataTable, search input would lose its focus after each entered character.
  // We need to rerender DataTable because there is a bug regarding virtual scrolling -- if we scroll down and change
  // the filter so that there is no item in the current scroll position, then the table displays nothing and is stuck.
  // Maybe in future versions PrimeReact will fix that, after it we may render the header within DataTable.
  //
  // Besides globalFilter, also the number of inhibitors is in key. It's because parent may change the inhibitor list,
  // (e.g., show only associated drugs) and so lower the number of inhibitors which leads to the same bug as filtering.
  //
  // When migrating from v7 to v10, global filtering was changed from relying on PrimeReact to a custom implementation.
  // The reason is that DataTable doesn't take the globalFilter prop into account at the first render (on mount).
  // Once we re-mount each time the global filter changes, the filtering didn't work effectively at all.
  if (!props.screenInhibitors) {
    return null;
  }
  const printMaxInhibitors = 50;
  const printInhibitors = props.printInhibitors ?? props.screenInhibitors;
  return <>
    <div className="ll-screen">
      {renderHeader()}
      <DataTable<IResultInhibitor[]>
        value={filteredScreenInhibitors}
        dataKey="id"
        key={`${globalFilter}_${props.differenceFilter}_${props.screenInhibitors.length}`}
        scrollable scrollHeight={props.scrollHeight ?? '500px'}
        virtualScrollerOptions={{ itemSize: 35 }}
        className={classNames("p-datatable-sm ll-inhibitor-table", props.className)}
        removableSort
        loading={false}
        emptyMessage={props.emptyMessage(globalFilter)}
        {...dataTableProps as any}
      >
        {props.columnsBefore}
        <Column field="databaseLink.drugId" body={databaseBodyTemplate} header="Database" sortable style={{ width: '120px' }} />
        <Column field="name" body={nameBodyTemplate} header="Inhibitor Name" sortable />
        <Column field="bindingEnergy.wt" header="Wild Type" sortable style={{ width: '110px' }} headerClassName="ll-kcal-mol" />
        <Column field="bindingEnergy.mut" header="Mutant" sortable style={{ width: '110px' }} headerClassName="ll-kcal-mol" />
        <Column field="bindingEnergy.diff" body={diffBodyTemplate} header="Difference" sortable style={{ width: '110px' }} headerClassName="ll-kcal-mol" />
        {props.columnsAfter}
      </DataTable>
    </div><div className="ll-print">
      {printInhibitors.length > printMaxInhibitors && <p>
          The following table shows the first {printMaxInhibitors} inhibitors with the best mutant binding energy.
      </p>}
      <table className="ll-inhibitor-table">
        <thead>
        <tr>
          <th>Database</th>
          <th>Inhibitor Name</th>
          <th>Wild Type <span className="ll-kcal-mol">[kcal/mol]</span></th>
          <th>Mutant <span className="ll-kcal-mol">[kcal/mol]</span></th>
          <th>Difference <span className="ll-kcal-mol">[kcal/mol]</span></th>
        </tr>
        </thead>
        <tbody>
        {printInhibitors.slice(0, printMaxInhibitors).map(data => <tr key={data.id}>
          <td>{databaseBodyTemplate(data)}</td>
          <td>{nameBodyTemplate(data)}</td>
          <td>{data.bindingEnergy.wt}</td>
          <td>{data.bindingEnergy.mut}</td>
          <td>{diffBodyTemplate(data)}</td>
        </tr>)}
        </tbody>
      </table>
    </div>
  </>

}

const GLOBAL_FILTER_FIELDS = ['name', 'zincId', 'zincIdShort', 'drugbankId'] as const;

function useFilteredInhibitors(inhibitors: IResultInhibitor[], globalFilter: string) {
  const [tokens, setTokens] = useState<string[]>([]);

  const [updateTokens] = useMemo(() => {
    return debounce((globalFilter: string) => {
      const tokens = globalFilter.match(/\w+/g) ?? [];
      setTokens(tokens);
    }, 100);
  }, [setTokens]);

  useEffect(() => {
    updateTokens(globalFilter);
  }, [globalFilter, updateTokens]);

  return useMemo(() => {
    if (tokens.length === 0) {
      return inhibitors;
    }
    return inhibitors.filter(i => {
      for (const token of tokens) {
        for (const field of GLOBAL_FILTER_FIELDS) {
          const value = i[field];
          if (typeof value === 'string' && value.toLowerCase().includes(token.toLowerCase())) {
            return true;
          }
        }
      }
      return false;
    })
  }, [inhibitors, tokens]);

}

export const diffBodyTemplate = ({bindingEnergy, bindingEnergyDiffClass: diffClass}: IResultInhibitor) => {
  const icon =
    diffClass == ChangeEnum.DecreaseLarge    ? Icons.decreaseLarge :
    diffClass == ChangeEnum.DecreaseModerate ? Icons.decreaseModerate :
    diffClass == ChangeEnum.NoChange         ? Icons.noChange :
    diffClass == ChangeEnum.IncreaseModerate ? Icons.increaseModerate :
                                               Icons.increaseLarge;
  return <div style={{display: 'flex', alignItems: 'center', gap: '5px'}}>
    <div style={{minWidth: '25px', textAlign: 'center'}}>
      {icon}
    </div>
    <div>
      {bindingEnergy.diff}
    </div>
  </div>
};

export default InhibitorBaseTable;
